import { FC, useState, useContext, useEffect } from "react";
// Utils
import { CommonContext } from "config/commonProvider";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { useGetCurrentBusinessUnit, useHasFeature, useStateSpecies } from "hooks";
// Components
import { Button, Modal, Text } from "components";
import { TagSelect } from "components/Common/TagSelect";
import { MergeAnimalTagsInput, useGetAnimalsWithTagsLazyQuery, useMergeAnimalTagsMutation } from "generated/graphql";
import { ANIMAL_TAGS } from "tags/api/queries";
import { GET_MY_ANIMALS_LIST } from "api/MyLivestock/Animal/queries";
import { removeNothings } from "helpers/general";
import { AnimalWithTags } from "tags/types";
import { animalsWithoutTags } from "tags/helpers";
import { DocumentNode } from "graphql";
import { OperationVariables } from "@apollo/client";
import { useTags } from "tags/hooks/useTagsHook";
import styles from "./styles.module.scss";

interface AddSelectedAnimalsToGroupProps {
  animalIds: string[];
}

export const AddSelectedAnimalsToTag: FC<AddSelectedAnimalsToGroupProps> = ({ animalIds }) => {
  const [mergeAnimalTags, { loading }] = useMergeAnimalTagsMutation();
  const { id } = useGetCurrentBusinessUnit();
  const { active } = useStateSpecies();
  const [getAnimals, { data }] = useGetAnimalsWithTagsLazyQuery();
  const [selectedAnimals, setSelectedAnimals] = useState<Array<AnimalWithTags>>([]);
  const isServerPaginatedAnimalsList = useHasFeature("SERVER_SIDE_PAGINATION_FOR_ANIMALS_LIST");
  const { getTagById } = useTags();
  const [isTagRemovable, setIsTagRemovable] = useState<boolean>(true);

  useEffect(() => {
    if (animalIds.length > 0) {
      getAnimals({ variables: { businessUnitId: Number(id), animalIds } });
    }
  }, [animalIds, id, getAnimals]);

  useEffect(() => {
    if (data?.animals?.edges != null) {
      const animals = removeNothings(data?.animals?.edges);
      setSelectedAnimals(animals);
    }
  }, [data]);

  const [showModal, setShowModal] = useState(false);
  const [tagId, setTagId] = useState<string>("");
  const { showNotification } = useContext(CommonContext);

  const handleModalToggle = (): void => {
    setShowModal(!showModal);
  };

  const handleCancelClick = (event): void => {
    event.stopPropagation();

    handleModalToggle();
  };

  const handleShowError = (errorMessage: string): void => {
    showNotification({
      variant: "error",
      message: errorMessage,
    });

    setShowModal(false);
    setTagId("");
  };

  const handleConfirmClick = async (): Promise<void> => {
    const animalIdsToAdd = animalsWithoutTags(selectedAnimals, tagId)?.map((animal) => animal?.node?.id);

    try {
      const input: MergeAnimalTagsInput = {
        animals: animalIdsToAdd,
        tagValues: [{ tag: tagId, isPresent: true }],
      };

      const previousVariables = {
        businessUnitId: id,
        animalTypeIds: active?.id ? [Number(active?.id)] : undefined,
      };

      const queriesToRefetch: [{ query: DocumentNode; variables: OperationVariables }] = [
        { query: ANIMAL_TAGS, variables: { buId: id } },
      ];
      if (!isServerPaginatedAnimalsList) {
        queriesToRefetch.push({ query: GET_MY_ANIMALS_LIST, variables: { ...previousVariables } });
      }

      await mergeAnimalTags({
        variables: { input },
        refetchQueries: queriesToRefetch,
        update: (_, { data: mergeAnimalTagsData }) => {
          if (mergeAnimalTagsData?.mergeAnimalTags?.errors == null) {
            showNotification({
              message: "Tag added successfully",
            });

            setShowModal(false);
            setTagId("");
          } else {
            const errorMessages = mergeAnimalTagsData?.mergeAnimalTags?.errors.reduce<string[]>(
              (accumulator, error) => {
                if (error?.message) accumulator.push(error.message);
                return accumulator;
              },
              [],
            );

            handleShowError(errorMessages.join("; "));
          }
        },
      });
    } catch (error) {
      handleShowError("Error adding tag");
    }
  };

  const handleSetTags = (tag: string): void => {
    setTagId(tag);
  };

  useEffect(() => {
    const tag = getTagById(tagId);

    if (tag?.node?.schema?.isRemovable === false) {
      setIsTagRemovable(false);
    } else {
      setIsTagRemovable(true);
    }
  }, [tagId, getTagById]);

  return (
    <>
      <Button
        id={ELEMENTS_IDS.ANIMALS_ADD_TO_GROUP_BUTTON}
        caption="Add tag"
        onClick={handleModalToggle}
        data-testid="add-tag-button"
      />
      <Modal
        showCloseButton
        active={showModal}
        handleClose={handleModalToggle}
        title="Add tag"
        subTitle={`Please select a tag for these ${animalIds.length} animals. This will add the tags to all of the selected animals.`}
        actions={{
          secondary: {
            onClick: handleCancelClick,
            caption: "Cancel",
            "data-testid": "add-tag-cancel-btn",
          },
          primary: {
            onClick: handleConfirmClick,
            caption: "Confirm",
            requesting: loading,
            disabled: loading || !tagId,
            "data-testid": "add-tag-confirm-btn",
          },
        }}
      >
        <TagSelect value={tagId} animalIds={animalIds} onSelect={handleSetTags} />
        {!isTagRemovable && (
          <aside className={styles.warning}>
            <Text smallest className={styles.warningText}>
              Caution: Once applied, this tag can only be removed by Breedr.
            </Text>
          </aside>
        )}
      </Modal>
    </>
  );
};
